import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Clock, Eye, Flame, FileText, PauseCircle, PlayCircle, Sparkles } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";
import { StatusPill } from "@/components/lux/Badges";
import { useAdvertiser } from "./useAdvertiser";

const STATUS_MESSAGE = {
  unverified: {
    title: "Complete your verification",
    text: "We only need a document and a quick selfie. In the prototype, verification is mocked for safety.",
    cta: { to: "/advertiser/verify", label: "Start verification" },
  },
  no_listing: {
    title: "Create your listing",
    text: "Your listing stays hidden until a moderator approves it. Edits are hidden until re-approved.",
    cta: { to: "/advertiser/listing", label: "Create listing" },
  },
  in_review: {
    title: "Your listing is in review",
    text: "A moderator will decide shortly. You'll receive a notification when the decision is made.",
    cta: { to: "/advertiser/listing", label: "View submission" },
  },
  live: {
    title: "Your listing is live",
    text: "Thank you for keeping your profile accurate and discreet.",
    cta: { to: "/advertiser/listing", label: "Edit listing" },
  },
  live_update_in_review: {
    title: "Edit awaiting approval",
    text: "Your approved profile is still visible. The pending changes are hidden until a moderator approves them.",
    cta: { to: "/advertiser/listing", label: "View changes" },
  },
  changes_requested: {
    title: "Changes requested",
    text: "A moderator asked for changes. Open your listing to see the note and resubmit.",
    cta: { to: "/advertiser/listing", label: "Open listing" },
  },
  rejected: {
    title: "Not published",
    text: "Your latest submission was not accepted. You can edit and try again.",
    cta: { to: "/advertiser/listing", label: "Open listing" },
  },
  suspended: {
    title: "Account suspended",
    text: "Your profile isn't visible. You can submit one appeal, which a different staff member will review.",
    cta: { to: "/advertiser/appeal", label: "Appeal" },
  },
  reverify: {
    title: "Re-verification due",
    text: "It has been over 90 days since your last verification. A fresh selfie check keeps your profile active.",
    cta: { to: "/advertiser/verify", label: "Re-verify" },
  },
  expired: {
    title: "Listing expired",
    text: "Your profile isn't visible. Renew to publish again.",
    cta: { to: "/advertiser/listing", label: "Open listing" },
  },
  banned: {
    title: "Account unavailable",
    text: "This account is no longer available on LUX.",
  },
  paused: {
    title: "Listing paused",
    text: "Your profile and stories are hidden from the site. Your remaining listing days are frozen - flip the switch below whenever you're ready to come back.",
  },
  deletion_pending: {
    title: "Deletion scheduled",
    text: "Your profile is offline. Your account and data will be permanently erased at the end of the 30-day grace period unless you cancel.",
    cta: { to: "/advertiser/account", label: "Review or cancel" },
  },
  draft: {
    title: "Finish your listing",
    text: "Your draft isn't visible yet. Complete the required fields and submit for review.",
    cta: { to: "/advertiser/listing", label: "Continue draft" },
  },
};

function MetricTile({ label, value, hint, icon: Icon, testId }) {
  return (
    <div className="lux-card p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-lux-silver">
        <Icon className="h-4 w-4" aria-hidden="true" />
        <p className="lux-label">{label}</p>
      </div>
      <p className="mt-3 font-serif text-4xl text-lux-text">{value}</p>
      {hint && <p className="mt-1 text-xs text-lux-muted">{hint}</p>}
    </div>
  );
}

function PauseCard({ me }) {
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const paused = me.profile?.status === "paused";
  const canToggle = paused || me.profile?.can_pause;
  if (!canToggle) return null;

  const toggle = async (checked) => {
    // checked = visible. Switch off -> pause, on -> resume.
    setBusy(true);
    try {
      if (checked) {
        const { data } = await api.post("/advertiser/listing/resume");
        const days = Math.round((data.paused_seconds || 0) / 86400);
        toast.success("Your listing is back online", { description: days ? `${days} paused day${days === 1 ? "" : "s"} added back to your listing.` : "Visitors can see your profile again." });
      } else {
        await api.post("/advertiser/listing/pause");
        toast.success("Listing paused", { description: "Hidden from the site. Your remaining days are frozen until you resume." });
      }
      await client.invalidateQueries({ queryKey: ["advertiser", "me"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`lux-card flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6 ${paused ? "border-lux-warning/35" : ""}`} data-testid="dashboard-pause-card">
      <div className="flex min-w-0 items-start gap-3">
        {paused ? <PauseCircle className="mt-0.5 h-5 w-5 shrink-0 text-lux-warning" aria-hidden="true" /> : <PlayCircle className="mt-0.5 h-5 w-5 shrink-0 text-lux-success" aria-hidden="true" />}
        <div className="min-w-0">
          <p className="lux-label">Listing visibility</p>
          <h2 className="mt-1 font-serif text-xl text-lux-text" data-testid="dashboard-pause-title">{paused ? "Paused - hidden from the site" : "Visible to visitors"}</h2>
          <p className="mt-1 text-sm text-lux-muted">
            {paused
              ? `Paused since ${fmtDate(me.profile.paused_at, true)}. Flip the switch to come back - your frozen days are added back automatically.`
              : "Taking a break? Pause with one tap. Your profile and stories disappear instantly and your listing days stop counting."}
          </p>
        </div>
      </div>
      <label className="flex shrink-0 cursor-pointer items-center gap-3 self-start sm:self-center" htmlFor="pause-switch">
        <span className="text-sm text-lux-muted" data-testid="dashboard-pause-label">{paused ? "Paused" : "Live"}</span>
        <Switch
          id="pause-switch"
          checked={!paused}
          onCheckedChange={toggle}
          disabled={busy}
          aria-label={paused ? "Resume listing" : "Pause listing"}
          className="h-7 w-12 data-[state=checked]:bg-lux-success data-[state=unchecked]:bg-lux-warning/70 [&>span]:h-6 [&>span]:w-6 [&>span]:bg-[#0B0B0C] data-[state=checked]:[&>span]:translate-x-5"
          data-testid="dashboard-pause-switch"
        />
      </label>
    </section>
  );
}

export default function DashboardPage() {
  useSeo({ title: "Advertiser dashboard | LUX", ...PRIVATE_SEO });
  const { me, meLoading } = useAdvertiser();
  const { data: stats } = useQuery({
    queryKey: ["advertiser", "stats"],
    queryFn: async () => (await api.get("/advertiser/stats")).data,
    staleTime: 30_000,
    enabled: !!me,
  });

  if (meLoading || !me) {
    return <div className="text-sm text-lux-muted" data-testid="dashboard-loading">Loading your dashboard...</div>;
  }

  const info = STATUS_MESSAGE[me.status] || {
    title: "Welcome",
    text: "Everything's in order. Thanks for keeping your profile up to date.",
  };
  const verif = me.verification || {};

  return (
    <div className="space-y-8" data-testid="advertiser-dashboard">
      <section>
        <p className="lux-label">Signed in as</p>
        <h1 className="mt-2 font-serif text-4xl text-lux-text sm:text-5xl" data-testid="dashboard-email">{me.email_masked || me.email}</h1>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-lux-muted">
          <StatusPill status={me.status} />
          {me.is_demo && <span className="lux-tag-demo">Demo</span>}
          {verif.verified_at && (
            <span className="inline-flex items-center gap-1.5">
              <BadgeCheck className="h-3.5 w-3.5 text-lux-silver" aria-hidden="true" />
              Verified {fmtDate(verif.verified_at)}
            </span>
          )}
        </div>
      </section>

      <section className="lux-card flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6" data-testid="dashboard-status-card">
        <div>
          <p className="lux-label">Next step</p>
          <h2 className="mt-2 font-serif text-2xl text-lux-text">{info.title}</h2>
          <p className="mt-1 max-w-xl text-sm text-lux-muted">{info.text}</p>
        </div>
        {info.cta && (
          <Link to={info.cta.to} className="lux-btn lux-btn-primary self-start sm:self-center" data-testid="dashboard-cta-button">
            {info.cta.label} <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </section>

      <PauseCard me={me} />

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricTile label="Profile views (14d)" value={stats?.totals?.views ?? 0} icon={Eye} testId="metric-views" />
        <MetricTile label="Contact taps (14d)" value={stats?.totals?.contact_clicks ?? 0} icon={Sparkles} testId="metric-contacts" />
        <MetricTile label="Story views (14d)" value={stats?.totals?.story_views ?? 0} hint={`${me.stories_today}/${me.stories_limit} posted today`} icon={Flame} testId="metric-stories" />
        <MetricTile
          label="Verification"
          value={verif.days_ago !== null && verif.days_ago !== undefined ? `${verif.days_ago}d` : "\u2014"}
          hint={verif.overdue ? "Re-verification overdue" : verif.due_in_days !== null && verif.due_in_days !== undefined ? `${verif.due_in_days} days until re-check` : "Not yet verified"}
          icon={BadgeCheck}
          testId="metric-verification"
        />
      </section>

      <section className="grid gap-4 lg:grid-cols-3" aria-label="Quick links">
        <Link to="/advertiser/listing" className="lux-card group block p-5 transition-colors hover:border-lux-silver/55" data-testid="dashboard-link-listing">
          <FileText className="h-5 w-5 text-lux-silver" aria-hidden="true" />
          <p className="mt-4 font-serif text-xl text-lux-text">Your listing</p>
          <p className="mt-1 text-sm text-lux-muted">Edit your headline, description, photos and primary area.</p>
          <span className="mt-4 inline-flex items-center gap-1 text-sm text-lux-silver group-hover:text-lux-silverBright">Open <ArrowRight className="h-4 w-4" /></span>
        </Link>
        <Link to="/advertiser/stories" className="lux-card group block p-5 transition-colors hover:border-lux-silver/55" data-testid="dashboard-link-stories">
          <Flame className="h-5 w-5 text-lux-silver" aria-hidden="true" />
          <p className="mt-4 font-serif text-xl text-lux-text">Stories</p>
          <p className="mt-1 text-sm text-lux-muted">Post up to 3 per day. Stories expire 24h after approval.</p>
          <span className="mt-4 inline-flex items-center gap-1 text-sm text-lux-silver group-hover:text-lux-silverBright">Open <ArrowRight className="h-4 w-4" /></span>
        </Link>
        <Link to="/advertiser/notifications" className="lux-card group block p-5 transition-colors hover:border-lux-silver/55" data-testid="dashboard-link-notifications">
          <Clock className="h-5 w-5 text-lux-silver" aria-hidden="true" />
          <p className="mt-4 font-serif text-xl text-lux-text">Notifications</p>
          <p className="mt-1 text-sm text-lux-muted">{me.unread_notifications ? `${me.unread_notifications} unread` : "Up to date"}</p>
          <span className="mt-4 inline-flex items-center gap-1 text-sm text-lux-silver group-hover:text-lux-silverBright">Open <ArrowRight className="h-4 w-4" /></span>
        </Link>
      </section>

      {me.profile?.slug && me.listing?.has_approved && me.profile?.status !== "paused" && (
        <section className="lux-card p-5">
          <p className="lux-label">Public profile</p>
          <p className="mt-2 font-serif text-lg text-lux-text">Preview your live profile exactly as visitors see it.</p>
          <Link to={`/profile/${me.profile.slug}`} target="_blank" rel="noreferrer" className="lux-btn lux-btn-secondary mt-4" data-testid="dashboard-public-preview">
            Open public profile <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      )}
    </div>
  );
}
